import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { LockClosedIcon, TrophyIcon } from '@heroicons/react/24/outline';
import { Button, Chip } from '@leadsales/design-system/react';
import { useFirstWeekAnalytics } from 'src/hooks/analytics/firstWeek/useFirstWeekAnalytics.hook';
import { useWorkspaceContext } from 'src/providers/WorkspaceProvider/Workspace.context';
import Tour from '../Tour/Tour.component';
import { useFeatureFlagsContext } from '../../providers/FeatureFlagsProvider/FeatureFlags.context';
import { eAlertType } from '../../providers/ToastProvider/Toast.enum';
import { useToastContext } from '../../providers/ToastProvider/Toast.hook';
import {
  CHALLENGE_CATALOG,
  CHALLENGE_CENTER_NS,
  catalogForRole,
  isDevBuild,
} from './ChallengeCenter.constants';
import { useChallengeCenter } from './ChallengeCenter.hook';
import { useChallengeTour } from './ChallengeCenter.tour.hook';
import { CHALLENGE_TOURS } from './ChallengeCenter.tours';
import { tChallengeKey, tChallengeRole } from './ChallengeCenter.types';
import './ChallengeCenter.styles.scss';

/**
 * Centro de Retos (AGI-498): the "Tu primera semana" chip, next to the bell
 * (SAD D16). Launch catalog: owner/admin get eight missions, seller gets
 * three, every one completed by a real signal in workspace-service. The
 * owner/admin rows that need a WhatsApp API account stay locked behind
 * "conecta tu WhatsApp API" (the gate): a locked row shows no action, the
 * gate row says why, and the row itself rolls over (hover, focus or tap) to
 * say what unlocks it, slot-machine style. `dev-trigger`
 * stands in for the real events in dev builds only. Every mission except
 * `attend_leads` (a habit, not a control) has a guided tour in
 * `ChallengeCenter.tours.ts` — one active tour at a time, tracked by
 * `activeTourKey`.
 */
const ROLE_LABEL_KEY: Record<tChallengeRole, string> = {
  OWNER: 'role.owner',
  ADMIN: 'role.admin',
  SELLER: 'role.seller',
};

// Per workspace: a person who manages two workspaces sees two chips.
const chipViewedStorageKey = (workspaceId: string) =>
  `challenge_center_chip_viewed_${workspaceId}`;

function hasReportedChipView(workspaceId: string): boolean {
  try {
    return (
      window.sessionStorage.getItem(chipViewedStorageKey(workspaceId)) !== null
    );
  } catch {
    // Storage blocked (private window): the in-memory guard in the component
    // still holds within a mount.
    return false;
  }
}

function rememberChipView(workspaceId: string): void {
  try {
    window.sessionStorage.setItem(chipViewedStorageKey(workspaceId), '1');
  } catch {
    // Storage blocked: the next mount reports again, which is the safe side.
  }
}

export default function ChallengeCenter() {
  const { t } = useTranslation(CHALLENGE_CENTER_NS);
  const { isChallengeCompletionToastEnabled } = useFeatureFlagsContext();
  const { addAlert } = useToastContext();
  const { currentWorkspaceId } = useWorkspaceContext();
  const {
    trackChipViewed,
    trackPanelOpened,
    trackTourStarted,
    trackTourAbandoned,
    trackToastViewed,
  } = useFirstWeekAnalytics();
  const {
    isOpen,
    setIsOpen,
    isLoading,
    eligible,
    role,
    holdout,
    hasWindow,
    windowKind,
    weekEnded,
    feedback,
    giveFeedback,
    challenges,
    completedCount,
    total,
    gateKey,
    trigger,
    markPracticed,
    celebratedKey,
    dismissCelebration,
  } = useChallengeCenter();
  const rootRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  // Router state, not window.location: a "Ve a ..." link navigates client-side
  // and the panel stays open, so the row has to re-render into "Muéstrame
  // cómo" on that same route change. Reading window.location at render time
  // only updated when something else happened to re-render the chip.
  const { pathname: currentPath } = useLocation();
  // `nonce` (not just the key) so clicking the SAME mission's tour twice in a
  // row — "Practicar de nuevo" after already finishing it once — still
  // changes this state and re-fires the effect below; a bare key would no-op
  // on React's identical-value bailout the second time.
  // Locked rows a tap has rolled over to their unlock hint (touch has no
  // hover); hover and focus roll them in CSS.
  const [revealed, setRevealed] = useState<Set<tChallengeKey>>(() => new Set());
  const toggleRevealed = (key: tChallengeKey) =>
    setRevealed((prev) => {
      const nextSet = new Set(prev);
      if (nextSet.has(key)) nextSet.delete(key);
      else nextSet.add(key);
      return nextSet;
    });
  const [tourRequest, setTourRequest] = useState<{
    key: tChallengeKey;
    nonce: number;
  } | null>(null);
  const activeTourKey = tourRequest?.key ?? null;
  const showDevControls = isDevBuild();
  // One tour.started per run: reset when a run is requested, set on the
  // first coachmark shown. Also gates tour.abandoned, so an Escape while the
  // first target is still being looked for is not an abandonment of a tour
  // that never showed.
  const hasReportedTourStartRef = useRef(false);
  const reportedChipWorkspacesRef = useRef(new Set<string>());
  // The chip is retired after a "not interested" answer at the end of the week.
  const isChipRetired = weekEnded && feedback === 'not_interested';

  const tour = useChallengeTour(activeTourKey, {
    onComplete: () => {
      if (activeTourKey) void markPracticed(activeTourKey);
    },
    onStepEnter: () => {
      if (!activeTourKey || !holdout || hasReportedTourStartRef.current) return;
      hasReportedTourStartRef.current = true;
      trackTourStarted({
        challengeKey: activeTourKey,
        role,
        holdoutArm: holdout,
      });
    },
    onSkip: (exitStepId) => {
      if (!activeTourKey || !holdout || !exitStepId) return;
      if (!hasReportedTourStartRef.current) return;
      trackTourAbandoned({
        challengeKey: activeTourKey,
        step: exitStepId,
        role,
        holdoutArm: holdout,
      });
    },
    // A tour that stops before its last step used to just disappear. Say
    // where it stopped: the rest of those steps live on a control this
    // workspace does not have yet (a WhatsApp API chat, a bulk send).
    onEndedEarly: () => {
      addAlert({
        type: eAlertType.INFO,
        title: t('tour.endedEarly.title'),
        message: t('tour.endedEarly.body'),
      });
    },
  });

  // useTour's `start` closes over the steps useChallengeTour(activeTourKey)
  // resolved for the render this effect belongs to — waiting for the render
  // triggered by setTourRequest (rather than calling tour.start() straight
  // from the click handler) is what guarantees `start` sees THIS key's steps,
  // not whatever key was active (or none) when the click fired.
  useEffect(() => {
    if (tourRequest) tour.start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourRequest]);

  // "Misión cumplida" is the product's own success toast (ToastProvider, the
  // app's distribution of the DS `ls-toast-success`): same surface, position
  // and countdown as every other notification, nothing bespoke. The hook
  // decides that a mission completed during this session; the flag decides
  // whether the person hears about it; either way the key is consumed so it
  // cannot fire twice.
  useEffect(() => {
    if (!celebratedKey) return;
    if (isChallengeCompletionToastEnabled) {
      addAlert({
        type: eAlertType.SUCCESS,
        title: t('toast.top'),
        message: t(CHALLENGE_CATALOG[celebratedKey].done),
      });
      if (holdout) {
        trackToastViewed({ challengeKey: celebratedKey, holdoutArm: holdout });
      }
    }
    dismissCelebration();
  }, [
    celebratedKey,
    isChallengeCompletionToastEnabled,
    addAlert,
    dismissCelebration,
    t,
    holdout,
    trackToastViewed,
  ]);

  // `holdout` is null until the first state commits, and a refetch in flight
  // may change the role or the arm, so the view is reported only once the
  // state the payload stamps has settled. The dedupe key is written after the
  // emit, so an emit that throws leaves the next mount free to retry. The ref
  // keeps the dedupe when storage is blocked, since this effect re-runs on
  // every refetch and role change.
  useEffect(() => {
    if (!currentWorkspaceId || !holdout || isLoading) return;
    if (!eligible || isChipRetired) return;
    if (reportedChipWorkspacesRef.current.has(currentWorkspaceId)) return;
    if (hasReportedChipView(currentWorkspaceId)) return;
    try {
      trackChipViewed({ role, holdoutArm: holdout });
    } catch {
      return;
    }
    reportedChipWorkspacesRef.current.add(currentWorkspaceId);
    rememberChipView(currentWorkspaceId);
  }, [
    currentWorkspaceId,
    holdout,
    isLoading,
    eligible,
    isChipRetired,
    role,
    trackChipViewed,
  ]);

  function startTourFor(key: tChallengeKey) {
    // The panel itself can sit on top of (or right next to) the real target
    // — close it so the spotlight isn't guiding attention to something the
    // panel is covering.
    setIsOpen(false);
    hasReportedTourStartRef.current = false;
    setTourRequest({ key, nonce: Date.now() });
  }

  useEffect(() => {
    function onClickOutside(event: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [setIsOpen]);

  // Escape closes the panel, the way every popover on the platform should
  // (NN heuristic 3). While a tour is showing, Escape belongs to the tour.
  useEffect(() => {
    if (!isOpen) return undefined;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Escape') return;
      setIsOpen(false);
      // Back to the chip that opened it, so a keyboard user is not dropped
      // at the top of the page (NN heuristic 3, AGI-526 I1).
      rootRef.current?.querySelector<HTMLButtonElement>('.ChallengeCenter__Trigger')?.focus();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, setIsOpen]);

  if (!eligible) {
    return null;
  }

  // Day 8, answered "no": the chip retires. The answer is the data point
  // (Damián, 2026-09-28); the row stays on the window for analytics.
  if (isChipRetired) {
    return null;
  }

  const challengeByKey = new Map(challenges.map((c) => [c.key, c]));
  const orderedKeys = catalogForRole(role);
  const allDone = hasWindow && completedCount === total && total > 0;
  const heading =
    windowKind === 'm0_weekly' ? t('chip.weekly') : t('chip.firstWeek');
  const anyLocked = challenges.some((c) => c.locked);
  // The one row whose action is the primary (AGI-526): the first open,
  // unlocked mission with somewhere to go. With the gate open the panel shows
  // seven actions at once, so the eye needs a place to start; every other
  // row's action stays ghost (composition.md: one primary per container).
  // Day 8's question owns the primary while it shows.
  const nextKey = weekEnded
    ? null
    : (orderedKeys.find((key) => {
        const c = challengeByKey.get(key);
        return !!c && !c.completedAt && !c.locked && !!CHALLENGE_TOURS[key];
      }) ?? null);
  // "Practicado, todavía sin hacer de verdad" is said once, as the legend of
  // the half mark, not under every practiced row (AGI-526). Each row keeps it
  // for screen readers, since the mark itself is aria-hidden.
  const anyPracticed = challenges.some(
    (c) => !c.completedAt && !c.locked && !!c.practicedAt,
  );
  const renderMission = (key: tChallengeKey) => {
    const challenge = challengeByKey.get(key);
    if (!challenge) return null;
    const done = !!challenge.completedAt;
    const practiced = !!challenge.practicedAt;
    const locked = challenge.locked;
    const catalogEntry = CHALLENGE_CATALOG[key];
    const tourConfig = CHALLENGE_TOURS[key];
    const isOnRequiredPath =
      !!tourConfig && currentPath.includes(tourConfig.requiredPath);
    const isGate = key === gateKey;

    return (
      <li
        key={key}
        className={classNames('ChallengeCenter__Mission', {
          'ChallengeCenter__Mission--done': done,
          'ChallengeCenter__Mission--practiced': !done && practiced,
          'ChallengeCenter__Mission--locked': locked,
        })}
      >
        <div className="ChallengeCenter__MissionMain">
          <span
            className={classNames('ChallengeCenter__Mark', {
              'ChallengeCenter__Mark--done': done,
              // A row practiced before the gate re-locked it still reads
              // as locked: the lock wins over the half mark.
              'ChallengeCenter__Mark--practiced': !done && !locked && practiced,
              'ChallengeCenter__Mark--locked': locked,
            })}
            aria-hidden="true"
          >
            {done ? '✓' : locked ? (
              <LockClosedIcon className="ChallengeCenter__LockIcon" />
            ) : practiced ? '◐' : ''}
          </span>
          <div className="ChallengeCenter__Text">
            {locked ? (
              // A reel with two faces: the title, and what unlocks it. Hover
              // and focus roll it in CSS; a tap toggles it for touch. Both
              // faces are in the accessible name, so a screen reader hears
              // why the row is locked without rolling anything.
              <button
                type="button"
                className={classNames('ChallengeCenter__Reel', {
                  'ChallengeCenter__Reel--revealed': revealed.has(key),
                })}
                aria-pressed={revealed.has(key)}
                onClick={() => toggleRevealed(key)}
              >
                <span className="ChallengeCenter__ReelTrack">
                  <span className="ChallengeCenter__Label ChallengeCenter__ReelFace">
                    {t(catalogEntry.title)}
                  </span>
                  <span className="ChallengeCenter__ReelFace ChallengeCenter__ReelFace--hint">
                    {t('lock.unlock')}
                  </span>
                </span>
              </button>
            ) : (
              <div
                className={classNames('ChallengeCenter__Label', {
                  'ChallengeCenter__Label--done': done,
                })}
              >
                {t(done ? catalogEntry.done : catalogEntry.title)}
              </div>
            )}
            {isGate && !done && anyLocked && (
              // The one line that explains every lock below it.
              <div className="ChallengeCenter__Status">{t('lock.hint')}</div>
            )}
            {!done && challenge.progress && (
              <div className="ChallengeCenter__Status">
                {t('status.attendProgress', challenge.progress)}
              </div>
            )}
            {!done && !locked && practiced && (
              <span className="sr-only">{t('status.practiced')}</span>
            )}
          </div>
        </div>

        <div className="ChallengeCenter__Actions">
          {!done &&
            !locked &&
            tourConfig &&
            (isOnRequiredPath ? (
              <Button
                type="button"
                color={key === nextKey ? 'primary' : 'ghost'}
                size="xs"
                title={t('actions.showMeHint')}
                onClick={() => startTourFor(key)}
              >
                {practiced ? t('actions.practiceAgain') : t('actions.showMe')}
              </Button>
            ) : (
              <Button
                type="button"
                color={key === nextKey ? 'primary' : 'ghost'}
                size="xs"
                title={t('actions.goToHint')}
                onClick={() => navigate(tourConfig.requiredPath)}
              >
                {t(tourConfig.routeLabelKey)}
              </Button>
            ))}
          {showDevControls && !done && !locked && (
            <Button
              type="button"
              color="ghost"
              size="xs"
              title={t('dev.simulateHint')}
              onClick={() => void trigger(key)}
            >
              {t('dev.simulate')}
            </Button>
          )}
        </div>
      </li>
    );
  };

  return (
    <div
      ref={rootRef}
      className="ChallengeCenter"
      data-testid="challenge-center"
    >
      <Button
        type="button"
        size="sm"
        color={allDone ? 'success' : 'tertiary'}
        className="ChallengeCenter__Trigger"
        // The visible count is part of the name: a screen reader hears the
        // progress, not only "open your challenges" (AGI-526 I2).
        aria-label={
          hasWindow
            ? t('chip.ariaLabelProgress', { done: completedCount, total })
            : t('chip.ariaLabel')
        }
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        onClick={() => {
          if (!isOpen && holdout)
            trackPanelOpened({ role, holdoutArm: holdout });
          setIsOpen((prev) => !prev);
        }}
      >
        {allDone ? (
          <span aria-hidden="true">🎉</span>
        ) : (
          // Mobile only (see the stylesheet): what the chip is when its label
          // no longer fits.
          <TrophyIcon className="ChallengeCenter__TriggerIcon" aria-hidden="true" />
        )}
        {/* Below 480px the label hides and the chip keeps icon + count
            (AGI-526): the name still reaches screen readers via aria-label. */}
        <span className="ChallengeCenter__TriggerLabel font-semibold">
          {heading}
        </span>
        {hasWindow && (
          // md, not sm: sm sets the count in 10px (AGI-526).
          <Chip
            size="md"
            variant="filled"
            color={allDone ? 'success' : 'default'}
          >
            {completedCount}/{total}
          </Chip>
        )}
      </Button>

      {isOpen && (
        <div
          className="ChallengeCenter__Panel"
          role="dialog"
          aria-label={heading}
        >
          {role && (
            <p className="ChallengeCenter__Eyebrow">
              {t(ROLE_LABEL_KEY[role])}
            </p>
          )}
          <div className="ChallengeCenter__Header">
            {/* h2: the page's h1 is the workspace, so a panel title one level
                down keeps the outline without a jump (AGI-526). */}
            <h2 className="ChallengeCenter__Title">{heading}</h2>
            {isLoading && (
              <span className="loading loading-spinner loading-xs" />
            )}
          </div>
          {hasWindow && total > 0 && (
            <div className="ChallengeCenter__Summary">
              <progress
                className="ChallengeCenter__Progress"
                value={completedCount}
                max={total}
                aria-label={t('panel.progress', {
                  done: completedCount,
                  total,
                })}
              />
              <p className="ChallengeCenter__ProgressLabel">
                {t('panel.progress', { done: completedCount, total })}
              </p>
            </div>
          )}

          {hasWindow && weekEnded && (
            // Day 8. No second week ships yet, so instead of leaving the
            // missions on screen forever the panel asks, once, whether the
            // person wants more. Both answers are recorded; "no" retires
            // the chip on the next render.
            <div className="ChallengeCenter__WeekEnd" data-testid="challenge-week-end">
              {feedback === 'interested' ? (
                <p className="ChallengeCenter__WeekEndText">{t('weekEnd.thanks')}</p>
              ) : (
                <>
                  <p className="ChallengeCenter__WeekEndText">{t('weekEnd.question')}</p>
                  <div className="ChallengeCenter__WeekEndActions">
                    <Button
                      type="button"
                      color="primary"
                      size="sm"
                      onClick={() => void giveFeedback('interested')}
                    >
                      {t('weekEnd.yes')}
                    </Button>
                    <Button
                      type="button"
                      color="ghost"
                      size="sm"
                      onClick={() => void giveFeedback('not_interested')}
                    >
                      {t('weekEnd.no')}
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}

          {!hasWindow ? (
            <div className="ChallengeCenter__Empty">
              <p>
                {role === 'SELLER' ? t('noWindow.seller') : t('noWindow.owner')}
              </p>
              {showDevControls && role !== 'SELLER' && (
                <Button
                  type="button"
                  color="primary"
                  size="sm"
                  block
                  onClick={() => void trigger('connect_whatsapp')}
                >
                  {t('dev.connectWhatsApp')}
                </Button>
              )}
              {showDevControls && (
                // A seller's window only opens on the real activation event,
                // which never fires twice locally; the dev reset opens a fresh
                // week for either role, so it belongs here too.
                <Button
                  type="button"
                  color="ghost"
                  size="xs"
                  block
                  onClick={() => void trigger('reset')}
                >
                  {t('dev.reset')}
                </Button>
              )}
            </div>
          ) : (
            <>
              <ul className="ChallengeCenter__List">
                {orderedKeys.map(renderMission)}
              </ul>
              {anyPracticed && (
                <p
                  className="ChallengeCenter__Legend"
                  aria-hidden="true"
                  data-testid="challenge-practiced-legend"
                >
                  <span className="ChallengeCenter__Mark ChallengeCenter__Mark--practiced">
                    ◐
                  </span>
                  {t('status.practiced')}
                </p>
              )}

              {showDevControls && (
                <div className="ChallengeCenter__Dev">
                  <p className="ChallengeCenter__DevCaption">{t('dev.only')}</p>
                  {windowKind === 'first_week' && !weekEnded && (
                    <Button
                      type="button"
                      color="ghost"
                      size="xs"
                      block
                      title={t('dev.endWeekHint')}
                      onClick={() => void trigger('end_week')}
                    >
                      {t('dev.endWeek')}
                    </Button>
                  )}
                  {windowKind === 'first_week' && (
                    <Button
                      type="button"
                      color="ghost"
                      size="xs"
                      block
                      title={t('dev.advanceWeekHint')}
                      onClick={() => void trigger('advance_week')}
                    >
                      {t('dev.advanceWeek')}
                    </Button>
                  )}
                  <Button
                    type="button"
                    color="ghost"
                    size="xs"
                    block
                    onClick={() => void trigger('reset')}
                  >
                    {t('dev.reset')}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {tour.isActive && (
        <Tour
          step={tour.currentStep}
          targetRect={tour.targetRect}
          stepIndex={tour.currentStepIndex}
          totalSteps={tour.totalSteps}
          onNext={tour.next}
          onPrev={tour.prev}
          onSkip={tour.skip}
          nextLabel={t('tour.next')}
          backLabel={t('tour.back')}
          skipLabel={t('tour.skip')}
          doneLabel={t('tour.done')}
        />
      )}
    </div>
  );
}
